"use client";

import { useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { FullPageMessage } from "@/components/layouts/FullPageMessage/FullPageMessage";
import {
  getMessages,
  isLocale,
  DEFAULT_LOCALE,
  type Locale,
} from "@/lib/i18n";

/**
 * 404 page dla rout w segmencie `[locale]` — locale-aware, używa
 * `FullPageMessage` jako standardowego komponentu blokującego.
 *
 * **Dlaczego Client Component:** Next.js `not-found.tsx` NIE dostaje route
 * params (params są dostępne tylko w page/layout). Żeby uzyskać locale do
 * lokalizacji, musimy odczytać URL pathname po stronie klienta przez
 * `usePathname()` i wydobyć pierwszy segment (`/pl/...` -> `pl`).
 *
 * **Fallback:** gdy pierwszy segment URL nie pasuje do żadnego znanego locale
 * (np. visitor trafił na `/foo/bar` jakimś dziwnym kanałem), fallback do
 * `DEFAULT_LOCALE` (`pl`).
 *
 * @see app/not-found.tsx — root fallback (poza segmentem `[locale]`)
 * @see FullPageMessage — abstrakcja pełnoekranowych komunikatów
 */
export default function LocaleNotFound() {
  const pathname = usePathname();
  const segments = pathname.split("/").filter(Boolean);
  const localeCandidate = segments[0];
  const locale: Locale = isLocale(localeCandidate)
    ? localeCandidate
    : DEFAULT_LOCALE;

  const t = getMessages(locale).notFound;

  /* 404 reporter - wysyła URL do backend m_seo > Błędy 404 dla audit przez admina.
     Fire-and-forget przez proxy /api/seo/404 (server-side dorzuca X-API-Key).
     Fail-soft - nigdy nie crash'uje 404 page. */
  useEffect(() => {
    fetch("/api/seo/404", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        url: pathname,
        referer: document.referrer || null,
        user_agent: navigator.userAgent,
      }),
    }).catch(() => {
      /* Silent fail - klient widzi już 404 page, nie ma sensu duplikować błędu. */
    });
  }, [pathname]);

  return (
    <FullPageMessage
      title="404"
      subtitle={t.title}
      body={t.body}
      icon="warning"
      variant="info"
      action={<Link href={`/${locale}`}>{t.homeLink}</Link>}
    />
  );
}
